import api from '../../utils/api';

export type MasterAdminAmbulanceRouteStop = {
  label?: string;
  address: string;
  lat?: number;
  lng?: number;
};

export type CreateMasterAdminAmbulanceBookingPayload = {
  patientName: string;
  patientPhone: string;
  patientGender?: 'MALE' | 'FEMALE' | 'CHILD' | null;
  vendorId?: string | null;
  customAgencyName?: string | null;
  routeStops: MasterAdminAmbulanceRouteStop[];
  ambulanceType: string;
  facilities?: string[];
  isReturnJourney?: boolean;
  isDeadBody?: boolean;
  pickupTimeType: 'IMMEDIATE' | 'SCHEDULED';
  scheduledPickupAt?: string | null;
  baseFare: number;
  discountAmount?: number;
  paymentCollectionType: 'FULL' | 'ADVANCE';
  advancePaidAmount?: number;
  paymentMethod?: 'CASH' | 'ONLINE';
};

export type MasterAdminAmbulanceBookingResult = {
  bookingId: string;
  bookingSource: string;
  vendorId: string | null;
  customAgencyName: string | null;
  agencyDisplayName: string | null;
  patientName: string;
  patientPhone: string;
  totalPayableAmount: number;
  paymentStatus: string;
  bookingStatus: string;
};

export type MasterAdminAmbulanceBookingRow = {
  bookingId: string;
  vendorId: string | null;
  customAgencyName: string | null;
  agencyDisplayName: string | null;
  patientName: string;
  patientPhone: string;
  patientGender: string | null;
  pickupAddress: string;
  dropAddress: string;
  routeStops: MasterAdminAmbulanceRouteStop[] | null;
  ambulanceType: string;
  facilities: string[];
  isDeadBody: boolean;
  isReturnJourney: boolean;
  pickupTimeType: string;
  scheduledPickupAt: string | null;
  baseFare: number;
  discountAmount: number;
  totalPayableAmount: number;
  paymentCollectionType: string | null;
  advancePaidAmount: number;
  remainingAmount: number;
  paymentStatus: string;
  paymentMethod: string;
  bookingStatus: string;
  createdAt: string;
  updatedAt: string;
};

export async function createMasterAdminAmbulanceBooking(
  payload: CreateMasterAdminAmbulanceBookingPayload
): Promise<{ success: boolean; message?: string; data: MasterAdminAmbulanceBookingResult }> {
  const response = await api.post('/master-admin/ambulance-bookings', payload);
  return response.data;
}

export async function listMasterAdminAmbulanceBookings(params?: {
  page?: number;
  limit?: number;
  search?: string;
}): Promise<{
  success: boolean;
  data: {
    bookings: MasterAdminAmbulanceBookingRow[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  };
}> {
  const { page = 1, limit = 50, search = '' } = params || {};
  const response = await api.get('/master-admin/ambulance-bookings', {
    params: { page, limit, search: search || undefined },
  });
  return response.data;
}

export async function updateMasterAdminAmbulanceBookingPayment(
  bookingId: string,
  payload: {
    baseFare?: number;
    discountAmount?: number;
    paymentCollectionType?: 'FULL' | 'ADVANCE';
    advancePaidAmount?: number;
    markRemainingPaid?: boolean;
  }
) {
  const response = await api.patch(
    `/master-admin/ambulance-bookings/${encodeURIComponent(bookingId)}/payment`,
    payload
  );
  return response.data;
}

export async function updateMasterAdminAmbulanceBooking(
  bookingId: string,
  payload: CreateMasterAdminAmbulanceBookingPayload
): Promise<{ success: boolean; message?: string; data?: { bookingId: string } }> {
  const response = await api.patch(
    `/master-admin/ambulance-bookings/${encodeURIComponent(bookingId)}`,
    payload
  );
  return response.data;
}

export async function deleteMasterAdminAmbulanceBooking(
  bookingId: string
): Promise<{ success: boolean; message?: string }> {
  const response = await api.delete(`/master-admin/ambulance-bookings/${encodeURIComponent(bookingId)}`);
  return response.data;
}
